import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue';
import { MonetizationType, Offer, PromotedBundleEdge } from '@/@types/graphql-types';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import {
	SnowplowClickoutContextGraphql,
	SnowplowContext,
	SnowplowPackageContext,
	SnowplowTitleContextGraphql,
} from '@/helpers/tracking/providers';
import { OfferLocation, trackClickOutGraphql } from '@/helpers/offer-helper';
import { BuyBoxFilterOptions } from '@/stores/user.store';
import { useLanguageStore } from '@/helpers/composables/useStores';
import { subscriptionPricesByCountry } from '@/components/buybox-new/new-buybox-config';
import { allowedPromotionLanguages, usePromotion } from '@/helpers/composables/usePromotion';
import { UpcomingReleaseBuybox } from '@/components/buybox-new/BuyBoxUpcomingNew.vue';
import { FALLBACK_PROMOTION_OFFER } from '@/helpers/promotion-helper';

// AQTV_TITLEDETAIL_PLACEMENTS
import AQTVTitleDetailPlacements, {
	AQTVTitleDetailPlacementsVariants,
} from '@/components/experiments/AQTVTitleDetailPlacememts';

// AQTV_TITLEDETAIL_PLACEMENTS

type BuyboxRowOffer = Offer & {
	icons?: string[];
	bundleId?: number;
	bundleTechnicalName?: string;
	rowType?: 'freeTrial' | null;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'RegularBuyBoxNew',
  props: {
    offers: null,
    bundles: null,
    filter: null,
    title: null,
    additionalContexts: null,
    isAVODActive: { type: Boolean },
    appleQualityTvExpVariant: null,
    upcomingReleases: null,
    isTitleUpcoming: { type: Boolean },
    isInWatchlist: { type: Boolean },
    seasonNumber: null,
    episodeNumber: null,
    isEpisodeBuybox: { type: Boolean }
  },
  emits: ["onNotifyMe"],
  setup(__props: any) {

const props = __props as {
	offers: {
		offerCount: number;
		flatrate: Offer[];
		free: Offer[];
		fast: Offer[];
		rent: Offer[];
		buy: Offer[];
		cinema: Offer[];
	};
	bundles: PromotedBundleEdge[];
	filter: BuyBoxFilterOptions;
	title: TitleDetail;
	additionalContexts: SnowplowContext[];
	isAVODActive: boolean;
	// AQTV_TITLEDETAIL_PLACEMENTS
	appleQualityTvExpVariant?: AQTVTitleDetailPlacementsVariants | null;
	// AQTV_TITLEDETAIL_PLACEMENTS
	upcomingReleases: UpcomingReleaseBuybox[];
	isTitleUpcoming: boolean;
	isInWatchlist: boolean;
	seasonNumber?: number | null;
	episodeNumber?: number | null;
	isEpisodeBuybox?: boolean;
};

const BuyBoxUpcomingNew = () => import('@/components/buybox-new/BuyBoxUpcomingNew.vue');
const BuyBoxOfferNew = () => import('@/components/buybox-new/BuyBoxOfferNew.vue');
const BuyBoxNoOffersNew = () => import('@/components/buybox-new/BuyBoxNoOffersNew.vue');
const BuyBoxNotifyMeNew = () => import('@/components/buybox-new/BuyBoxNotifyMeNew.vue');
const BuyBoxPromotionRowNew = () => import('@/components/buybox-new/BuyBoxPromotionRowNew.vue');





const { language } = useLanguageStore();
const buyboxContainer = ref<HTMLElement | null>(null);

const showFreeTrial = computed(
	() =>
		!!promotionOffer.value &&
		[BuyBoxFilterOptions.ALL, BuyBoxFilterOptions.FREE, BuyBoxFilterOptions.SUBSCRIPTION].includes(props.filter) &&
		// splitting language for locale specific ones like Spanish - Spain, Spanish - Latin America, etc
		allowedPromotionLanguages.includes(language.value.split('-')[0])
);

const showNoOffers = computed(() => {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { offerCount, free, ...others } = props.offers;
	return props?.filter === BuyBoxFilterOptions.FREE
		? free.length === 0
		: Object.values(others).every(el => el.length === 0);
});

const allOffers = computed(() => props.offers.free.concat(props.offers.flatrate) ?? []);

//temp hide AMZ titles on apple
const hasApple = computed(() => allOffers.value.some(offer => offer.package.packageId === 350));
//temp hide AMZ titles on apple

const flatrateOffers = computed<BuyboxRowOffer[]>(() => {
	if (showNoOffers.value) return [];
	const sortedFlatrateOffers = props.offers.flatrate
		.filter(
			// We need to exclude ADS offers here because they appear in both the flatrate and free offer arrays for some reason and cause duplicates in the buybox
			offer => offer.monetizationType !== 'FREE' && offer.monetizationType !== 'ADS' && showOffer(offer)
		)
		.sort((a, b) => {
			const offerFreeTrialA = subscriptionPricesByCountry['GB']
				.find(provider => {
					return provider.id === a?.package.packageId;
				})
				?.free_trial?.split(' ')[0];

			const offerFreeTrialB = subscriptionPricesByCountry['GB']
				.find(provider => {
					return provider.id === b?.package.packageId;
				})
				?.free_trial?.split(' ')[0];

			const offerPriceA = subscriptionPricesByCountry['GB'].find(provider => {
				return provider.id === a?.package.packageId;
			})?.price;
			const offerPriceB = subscriptionPricesByCountry['GB'].find(provider => {
				return provider.id === b?.package.packageId;
			})?.price;

			// Sorts array by two dimensions - first the longest free trial length, then the cheapest price.
			return Number(offerFreeTrialB) - Number(offerFreeTrialA) || Number(offerPriceA) - Number(offerPriceB);
		});
	return sortedFlatrateOffers;
});

const nonFlatrateOffers = computed<BuyboxRowOffer[]>(() => {
	if (showNoOffers.value) return [];
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { offerCount, flatrate, ...others } = props.offers;
	const rowOffers = [...Object.values(others).flat()];

	// START AVOD
	// Only show One Plex offer at a time.
	// + Always filtering out Justwatch-plex Provider as it's only used by partners.
	const AVODTechnicalName = props.isAVODActive
		? ['plex', 'justwatchplexchannel']
		: ['plexplayer', 'justwatchplexchannel'];

	const filteredOffers = rowOffers
		.filter(el => !AVODTechnicalName.includes(el.package.technicalName) && showOffer(el))
		.concat(bundleOffers.value);

	return filteredOffers;
});

//  Quality TV
const qualityTvTrackingContexts = computed(() => {
	const titleObjectId = props.title.__typename === 'Season' ? props.title.show.objectId : props.title.objectId;
	const titleObjectType = props.title.__typename === 'Season' ? props.title.show.objectType : props.title.objectType;
	const titleContext = new SnowplowTitleContextGraphql(
		titleObjectId,
		titleObjectType,
		props.seasonNumber ?? null,
		props.episodeNumber ?? null
	);

	return (props.additionalContexts || []).concat([titleContext]);
});
// Apple Quality TV

const bundleOffers = computed<BuyboxRowOffer[]>(() => {
	if (![BuyBoxFilterOptions.SUBSCRIPTION, BuyBoxFilterOptions.ALL].includes(props?.filter)) return [];
	return props.bundles.map(bundle => {
		return {
			...bundle.offer,
			standardWebURL: bundle.promotionUrl,
			bundleId: bundle.node.bundleId,
			bundleTechnicalName: bundle.node.technicalName,
			icons: bundle.node.packages.map(pkg => pkg.iconWide),
		} as BuyboxRowOffer;
	});
});

const { promotionOffer, promotionCatalogue, inPromotionCatalogue } = usePromotion({
	title: () => props.title,
	offers: () => allOffers.value,
});

const _allOffers = computed(() => {
	const offers = [...flatrateOffers.value, ...nonFlatrateOffers.value];

	// JUSTWATCH_TV
	const indexOfJustWatchTvOffer = offers.findIndex(offer => offer.package.technicalName === 'justwatchtv');
	const indexOfFirstFreeOffer = offers.findIndex(offer =>
		[MonetizationType.Ads, MonetizationType.Free].includes(offer.monetizationType)
	);
	if (indexOfJustWatchTvOffer > indexOfFirstFreeOffer) {
		const justWatchTVhOffer = offers.splice(indexOfJustWatchTvOffer, 1)[0];
		offers.splice(indexOfFirstFreeOffer, 0, justWatchTVhOffer);
	}
	// JUSTWATCH_TV

	//temp hide AMZ titles on apple
	const offersFilteredForAMZ = hasApple.value ? offers.filter(offer => offer.package.packageId !== 9) : offers;
	//temp hide AMZ titles on apple
	if (
		!showNoOffers.value &&
		[BuyBoxFilterOptions.ALL, BuyBoxFilterOptions.SUBSCRIPTION, BuyBoxFilterOptions.FREE].includes(props.filter) &&
		!!promotionOffer.value
	) {
		if (offersFilteredForAMZ.length > 2) {
			offersFilteredForAMZ.splice(2, 0, { ...promotionOffer.value, rowType: 'freeTrial' } as BuyboxRowOffer);
		} else {
			offersFilteredForAMZ.push({ ...promotionOffer.value, rowType: 'freeTrial' } as BuyboxRowOffer);
		}
	}

	return offersFilteredForAMZ;
});

function showOffer(offer: Offer) {
	if (props?.filter === BuyBoxFilterOptions.ALL) return true;
	if (props?.filter === BuyBoxFilterOptions.SUBSCRIPTION) {
		return offer?.monetizationType === 'FLATRATE';
	}
	if (props?.filter === BuyBoxFilterOptions.FREE) {
		return ['FREE', 'ADS', 'FAST'].includes(offer?.monetizationType);
	}
	if (props?.filter === BuyBoxFilterOptions.BUY) {
		return ['BUY', 'FLATRATE_AND_BUY'].includes(offer?.monetizationType);
	}
	if (props?.filter === BuyBoxFilterOptions.RENT) {
		return offer?.monetizationType === 'RENT';
	}
	if (props?.filter === BuyBoxFilterOptions.CINEMA) {
		return offer?.monetizationType === 'CINEMA';
	}

	if (props?.filter === BuyBoxFilterOptions.FAST_TV) {
		return offer?.monetizationType === 'FAST';
	}
}

function clickOut(payload: {
	buyboxOffer: BuyboxRowOffer;
	isLeavingIconShown: boolean;
	offerLocation?: OfferLocation;
	row: HTMLElement | null;
}) {
	let _offer = payload.buyboxOffer;
	const titleObjectId = props.title.objectId;
	const titleObjectType = props.title.objectType;

	let contexts: SnowplowContext[] = [];
	const packageName =
		payload.buyboxOffer.package.technicalName ??
		promotionCatalogue.value.get(payload.buyboxOffer.package.packageId)?.technicalName ??
		'';
	const packageContext = new SnowplowPackageContext(
		payload.buyboxOffer.bundleId ?? payload.buyboxOffer.package.packageId,
		payload.buyboxOffer.bundleTechnicalName ?? packageName,
		payload.buyboxOffer.bundleId ? 'bundle' : 'package'
	);

	contexts = [...props.additionalContexts, packageContext];

	const rawIndex = Array.from(buyboxContainer.value?.querySelectorAll('.offer-container') ?? [])?.indexOf(
		payload.row as HTMLElement
	);

	trackClickOutGraphql({
		offer: _offer,
		objectType: titleObjectType,
		objectId: titleObjectId,
		offerLocation: payload.offerLocation ?? OfferLocation.REGULAR_BUYBOX,
		additionalContexts: contexts,
		seasonNumber: props.seasonNumber ?? null,
		episodeNumber: props.episodeNumber ?? null,
		contentType: undefined,
		filterOption: props.filter,
		rowNumber: rawIndex > -1 ? rawIndex + 1 : undefined,
	});

	const contextsWithRowData: SnowplowContext[] = [
		SnowplowClickoutContextGraphql.fromProviderOffer(
			_offer,
			undefined,
			props.filter,
			rawIndex > -1 ? rawIndex + 1 : undefined
		),
		new SnowplowTitleContextGraphql(titleObjectId, titleObjectType),
		...(contexts ? contexts : []),
	];

	// trackTitleDetailRedesignNewBuyBoxExp({
	// 	action: 'click',
	// 	label: getExperimentClickoutLabel(payload.offerLocation),
	// 	contexts: contextsWithRowData,
	// 	value: getExperimentOfferLocation(payload.offerLocation),
	// });

	// AQTV_TITLEDETAIL_PLACEMENTS
	if (!!props.appleQualityTvExpVariant && props.title.__typename === 'Movie') {
		AQTVTitleDetailPlacements.trackEvent({
			action: 'click',
			label: 'clickout_buybox',
			contexts: contextsWithRowData,
		});
	}
	// AQTV_TITLEDETAIL_PLACEMENTS
}

return { __sfc: true,BuyBoxUpcomingNew, BuyBoxOfferNew, BuyBoxNoOffersNew, BuyBoxNotifyMeNew, BuyBoxPromotionRowNew, props, language, buyboxContainer, showFreeTrial, showNoOffers, allOffers, hasApple, flatrateOffers, nonFlatrateOffers, qualityTvTrackingContexts, bundleOffers, promotionOffer, promotionCatalogue, inPromotionCatalogue, _allOffers, showOffer, clickOut }
}

})